/**
 * @file billReadLines.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-16
 * @modified 2026-10-16
 * @description A bill's lines as the PDF reader gave them: a line it read
 *              twice, and each line's VAT (Cam, 16 Oct 2026 — Burcombe's
 *              QA03476: "added a duplicate of the very last bill row's last
 *              sub line … and the VAT did not get put in from the item's
 *              purchase VAT stored in inventory, nor did it see from the bill
 *              that VAT was on every line … the fact that there is a VAT
 *              total line on the bill PDF").
 *
 *   A LINE READ TWICE (`dropRepeatedLines`). The reader is a model; on a long
 *   bill of jobs it can list the table's last charge line twice. Nothing on
 *   the screen or the server adds a line, so the repeat is caught where the
 *   read arrives, ON EVIDENCE: a line exactly like the one before it in the
 *   same job (same words, figures, job, references, address and date) is left
 *   out only when the lines with it come to MORE than the invoice's net and
 *   without the repeats they come to it to the penny. Without the invoice's
 *   net, or when the figures do not say so, both stay and the note says to
 *   check them (two identical charges on one job can be real).
 *
 *   EACH LINE'S VAT, in this order (`defaultTaxType`; Cam, 16 Oct 2026 — PO
 *   first, as every field of a line on a PO line):
 *     1. the PO line's tax type, when the line is on a PO line that has one
 *        ('NONE' or blank is none: a PO made here saves NONE when nobody
 *        chose — its item says what it really is);
 *     2. the item's purchase tax type (inventory.items.purchase_tax_type —
 *        EXTH (Bag) / (Bulk) / FS carry INPUT2);
 *     3. what the BILL says (`billTaxOf`): the line's own VAT % where the
 *        reader read one; else, when the invoice prints a VAT total, one rate
 *        for every line if that total is one rate of its net — 20 % → INPUT2,
 *        5 % → RRINPUT, £0 → ZERORATEDINPUT — within the pennies per-line
 *        rounding makes (`invoiceTaxRule`); a mix is left to the user, said.
 *     4. a PO's or item's 'NONE' as it stands; else nothing (the user picks).
 *
 *   Pure. Generated to utils/billReadLines.js for the PDF reader
 *   (node scripts/sync-shared-utils.js) — edit this copy.
 */

/** A tax type that says something ('NONE' and blank say nothing). */
export function hasTax(taxType) {
  const t = String(taxType ?? '').trim().toUpperCase();
  return t !== '' && t !== 'NONE';
}

const RATE_OF = Object.freeze({ INPUT2: 0.2, INPUT: 0.2, CAPEXINPUT2: 0.2, RRINPUT: 0.05, ZERORATEDINPUT: 0, EXEMPTINPUT: 0, NONE: 0 });
/** The rate a purchase tax type charges (0 when unknown). */
export const rateOf = (taxType) => RATE_OF[String(taxType ?? '').trim().toUpperCase()] ?? 0;

const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(String(v).replace(/[£,\s]/g, ''));
  return Number.isFinite(n) ? n : null;
};
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const money = (n) => `£${round2(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** A VAT % as printed → the purchase tax type; null when none was read. */
export function taxTypeForVatPercent(pct) {
  const p = num(pct);
  if (p === null) return null;
  if (Math.abs(p - 20) < 0.5) return 'INPUT2';
  if (Math.abs(p - 5) < 0.5) return 'RRINPUT';
  if (Math.abs(p) < 0.005) return 'ZERORATEDINPUT';
  return null;
}

/** A read line's net amount: its own, else quantity × price, else its price (a charge printed as an amount). */
export function readLineAmount(l) {
  const amount = num(l?.line_amount);
  if (amount !== null) return amount;
  const unit = num(l?.unit_amount ?? l?.unit_price);
  const qty = num(l?.quantity);
  if (unit === null) return null;
  return round2((qty === null ? 1 : qty) * unit);
}

/**
 * One rate for every line, from the invoice's VAT total against its net.
 *
 * @param {{ netTotal?: number|null, totalTax?: number|null, amounts?: number[] }} p
 *        netTotal — the invoice's net (else the lines' total); amounts — the lines' nets (for the rounding allowance)
 * @returns {{ taxType: string, rate: number, reason: string } | { taxType: null, mixed: true, reason: string } | null}
 */
export function invoiceTaxRule({ netTotal = null, totalTax = null, amounts = [] } = {}) {
  const tax = num(totalTax);
  if (tax === null) return null;
  const list = (amounts || []).map(num).filter(n => n !== null);
  const net = num(netTotal) ?? (list.length ? round2(list.reduce((s, n) => s + n, 0)) : null);
  if (net === null || !(Math.abs(net) > 0)) return null;
  // Each line's VAT is rounded to the penny (Xero's way), so the total can
  // be up to half a penny a line from rate × net.
  const allowance = Math.max(0.02, 0.005 * Math.max(1, list.length) + 0.005);
  if (Math.abs(tax) < 0.005) {
    return { taxType: 'ZERORATEDINPUT', rate: 0, reason: `The invoice's VAT is ${money(0)} — no VAT on any line` };
  }
  for (const [taxType, rate, pct] of [['INPUT2', 0.2, '20%'], ['RRINPUT', 0.05, '5%']]) {
    if (Math.abs(tax - net * rate) <= allowance) {
      return { taxType, rate, reason: `The invoice's VAT ${money(tax)} is ${pct} of its ${money(net)} before VAT — ${pct} on every line` };
    }
  }
  return { taxType: null, mixed: true, reason: `The invoice's VAT ${money(tax)} is not one rate of its ${money(net)} before VAT — set each line's VAT` };
}

/**
 * What the BILL says each read line's VAT is: its own VAT % where read, else
 * the invoice's one rate (`invoiceTaxRule`), else nothing.
 *
 * @param {object[]} lines — the reader's lines (vat_percent, line_amount / unit_amount, quantity)
 * @param {{ subTotal?: number|null, totalTax?: number|null }} invoice
 * @returns {{ taxes: Array<{ value: string, from: 'line'|'invoice', reason: string }|null>, rule: object|null, note: string|null }}
 */
export function billTaxOf(lines, { subTotal = null, totalTax = null } = {}) {
  const list = Array.isArray(lines) ? lines : [];
  const own = list.map(l => taxTypeForVatPercent(l?.vat_percent));
  const missing = own.filter(t => !t).length;
  const rule = missing ? invoiceTaxRule({ netTotal: subTotal, totalTax, amounts: list.map(readLineAmount) }) : null;
  // A line's own % that disagrees with the invoice's one rate is kept: the
  // line says so. A mix with lines unread is left to the user.
  const taxes = own.map((t) => {
    if (t) return { value: t, from: 'line', reason: 'The VAT % printed on the line' };
    if (rule && rule.taxType) return { value: rule.taxType, from: 'invoice', reason: rule.reason };
    return null;
  });
  const note = missing && rule && rule.mixed
    ? `${rule.reason}${missing < list.length ? ` (${missing} of ${list.length} lines have no VAT % printed)` : ''}.`
    : null;
  return { taxes, rule, note };
}

/**
 * A line's tax type: the PO line's → the item's → the bill's → a 'NONE' as
 * it stands → nothing.
 * @returns {{ value: string|null, from: 'po'|'item'|'bill'|null }}
 */
export function defaultTaxType({ poTax = null, itemTax = null, billTax = null } = {}) {
  const up = (t) => (t === null || t === undefined || String(t).trim() === '' ? null : String(t).trim().toUpperCase());
  const po = up(poTax);
  const item = up(itemTax);
  const bill = up(billTax);
  if (hasTax(po)) return { value: po, from: 'po' };
  if (hasTax(item)) return { value: item, from: 'item' };
  if (bill) return { value: bill, from: 'bill' };
  if (po) return { value: po, from: 'po' };
  if (item) return { value: item, from: 'item' };
  return { value: null, from: null };
}

// ── A line read twice ──────────────────────────────────────────────────────

const words = (s) => String(s ?? '').replace(/\s+/g, ' ').trim().toLowerCase();
const refsOf = (l) => (Array.isArray(l?.references) ? l.references : []).map(r => words(r)).filter(Boolean).join('|');
/** Everything the reader says of a line, for "the same line again". */
function lineSig(l) {
  return [
    words(l?.item_code), words(l?.description), num(l?.quantity), num(l?.unit_amount ?? l?.unit_price), readLineAmount(l),
    words(l?.job_key), refsOf(l), words(l?.deliver_to), words(l?.job_date), words(l?.unit),
  ].map(v => (v === null ? '' : String(v))).join('#');
}

/**
 * Leave out a line the reader listed twice — only on the invoice's own figures.
 *
 * @param {object[]} lines — the reader's lines, in order
 * @param {{ subTotal?: number|null }} invoice — the invoice's net (before VAT)
 * @returns {{ lines: object[], dropped: number[], repeats: number[], note: string|null }}
 *          dropped — indexes left out; repeats — indexes that repeat the line before (left in or out)
 */
export function dropRepeatedLines(lines, { subTotal = null } = {}) {
  const list = Array.isArray(lines) ? lines : [];
  const repeats = [];
  for (let i = 1; i < list.length; i += 1) {
    const a = list[i - 1];
    const b = list[i];
    if (!a || !b || !words(b.description)) continue;
    if (lineSig(a) === lineSig(b)) repeats.push(i);
  }
  if (!repeats.length) return { lines: list, dropped: [], repeats, note: null };
  const what = (i) => {
    const l = list[i];
    const amt = readLineAmount(l);
    return `"${String(l.description).trim()}"${amt !== null ? ` ${money(amt)}` : ''}${l.job_key ? ` (${String(l.job_key).trim()})` : ''}`;
  };
  const net = num(subTotal);
  const total = round2(list.reduce((s, l) => s + (readLineAmount(l) || 0), 0));
  const excess = net === null ? null : round2(total - net);
  if (excess !== null && excess > 0.004) {
    // The latest repeats first (a model repeats the end of a table); take
    // only a set that makes the lines come to the invoice's net exactly.
    const out = [];
    let left = excess;
    for (const i of [...repeats].reverse()) {
      const amt = readLineAmount(list[i]) || 0;
      if (amt > 0 && amt <= left + 0.004) { out.push(i); left = round2(left - amt); }
      if (Math.abs(left) < 0.005) break;
    }
    if (out.length && Math.abs(left) < 0.005) {
      const dropped = out.sort((x, y) => x - y);
      const kept = list.filter((_, i) => !dropped.includes(i));
      return {
        lines: kept, dropped, repeats,
        note: `The reader listed ${dropped.map(what).join(', ')} twice; the repeat is left out — the lines now come to the invoice's ${money(net)} before VAT.`,
      };
    }
  }
  return {
    lines: list, dropped: [], repeats,
    note: `${repeats.map(what).join(', ')} ${repeats.length === 1 ? 'is' : 'are'} listed twice in a row — check the invoice and remove ${repeats.length === 1 ? 'it' : 'them'} if read twice.`,
  };
}

/**
 * Job suggestions (`reference_links.jobs`) after read lines were left out:
 * each job's `line_indexes` without the dropped lines, renumbered.
 */
export function jobsWithoutLines(jobs, dropped) {
  const gone = new Set(dropped || []);
  if (!gone.size) return jobs;
  const shift = (i) => i - [...gone].filter(d => d < i).length;
  return (jobs || []).map(j => ({
    ...j,
    line_indexes: (j.line_indexes || []).filter(i => !gone.has(i)).map(shift),
  })).filter(j => j.line_indexes.length);
}
